import { extractText } from "unpdf";
//#region src/ai.ts
/**
* AI 출력 공통 규격 — L3-PF-01 / AC-PF-01-04
*
* 제품 원칙 P2("모든 AI 출력에는 근거가 붙는다")를 타입 레벨에서 강제한다.
* 추천·리뷰·요약·Q&A 등 모든 AI 응답은 이 형태를 따라야 한다.
*/
const EVIDENCE_TYPES = [
	"asset",
	"document",
	"regulation",
	"rule"
];
function isNonEmpty(items) {
	return items.length > 0;
}
/** 근거가 하나도 없으면 던진다. 서비스 계층 반환 직전 게이트로 사용한다. */
function assertHasEvidence(evidences) {
	if (!isNonEmpty(evidences)) throw new Error("AiOutput requires at least one evidence (L3-PF-01 / AC-PF-01-04)");
	return evidences;
}
//#endregion
//#region src/time.ts
/**
* 시각 · D-day 계산 — L3-PF-01 / AC-PF-01-03
*
* 프로토타입은 화면마다 다른 날짜를 하드코딩했다(캘린더 07-25, Lab log 07-23, 회의록 07-23 …).
* 실제 제품은 **서버가 내려준 단일 기준 시각**에서만 D-day를 계산한다.
* 따라서 이 모듈의 함수는 전부 `now`를 인자로 받는다 — 내부에서 `new Date()`를 부르지 않는다.
*/
const MS_PER_DAY = 864e5;
/**
* 순간을 지정 타임존의 달력 날짜 `YYYY-MM-DD`로 바꾼다 (E14 §10).
* UTC ISO 문자열의 앞 10자는 UTC 날짜라 KST 00~09시의 순간이 전날로 보인다.
*/
function formatLocalDate(instant, timeZone = "Asia/Seoul") {
	const target = typeof instant === "string" ? new Date(instant) : instant;
	return new Intl.DateTimeFormat("en-CA", {
		timeZone,
		year: "numeric",
		month: "2-digit",
		day: "2-digit"
	}).format(target);
}
/** 해당 시각을 지정 타임존의 자정(UTC 기준 epoch ms)으로 내림한다. */
function startOfDayUtcMs(instant, timeZone) {
	return Date.parse(`${formatLocalDate(instant, timeZone)}T00:00:00Z`);
}
/**
* 남은 일수. 오늘이면 0, 내일이면 1, 어제면 -1.
* 달력 날짜 기준이므로 시:분에 영향을 받지 않는다.
*/
function daysUntil(deadline, now, timeZone = "Asia/Seoul") {
	const target = typeof deadline === "string" ? new Date(deadline) : deadline;
	const base = typeof now === "string" ? new Date(now) : now;
	return Math.round((startOfDayUtcMs(target, timeZone) - startOfDayUtcMs(base, timeZone)) / MS_PER_DAY);
}
/** `D-9`, `D-DAY`, `D+3` 표기. 표시 시점에 계산한다 — 하드코딩 금지. */
function formatDday(deadline, now, timeZone = "Asia/Seoul") {
	const days = daysUntil(deadline, now, timeZone);
	if (days === 0) return "D-DAY";
	return days > 0 ? `D-${days}` : `D+${Math.abs(days)}`;
}
/** 마감까지 남은 밀리초. 1초 주기 카운트다운(L2-06 §8)의 입력. */
function remainingMs(deadline, now) {
	const target = typeof deadline === "string" ? new Date(deadline) : deadline;
	const base = typeof now === "string" ? new Date(now) : now;
	return Math.max(0, target.getTime() - base.getTime());
}
/** 지정 타임존에서의 로컬 시(0~23)를 읽는다. */
function localHour(instant, timeZone) {
	const hour = new Intl.DateTimeFormat("en-US", {
		timeZone,
		hour: "numeric",
		hourCycle: "h23"
	}).format(instant);
	return Number(hour);
}
const MS_PER_MINUTE = 6e4;
const MS_PER_HOUR = 36e5;
/**
* 마지막 활동 경과 표기 — L3-ML-01 §4.2 / AC-ML-01-02·19.
* `now`는 항상 호출자가 넘긴다 (서버 값 또는 고정 시각) — 내부에서 시계를 읽지 않는다.
*/
function formatRelativeActivityTime(occurredAt, now, timeZone = "Asia/Seoul") {
	const target = typeof occurredAt === "string" ? new Date(occurredAt) : occurredAt;
	const base = typeof now === "string" ? new Date(now) : now;
	const elapsedMs = base.getTime() - target.getTime();
	if (elapsedMs < MS_PER_MINUTE) return "방금 전";
	if (elapsedMs < MS_PER_HOUR) return `${Math.floor(elapsedMs / MS_PER_MINUTE)}분 전`;
	if (elapsedMs < 24 * MS_PER_HOUR) return `${Math.floor(elapsedMs / MS_PER_HOUR)}시간 전`;
	const dayDiff = daysUntil(base, target, timeZone);
	if (dayDiff === 1) return localHour(target, timeZone) < 12 ? "어제 오전" : "어제 오후";
	return `${dayDiff}일 전`;
}
//#endregion
//#region src/calendar-date.ts
/**
* 달력 날짜(`YYYY-MM-DD`) 계산 — 날짜 선택기·달력 화면이 함께 쓴다.
*
* 값은 시간대 없는 달력 날짜다. 계산은 UTC 자정으로 고정한 `Date`에서만 해서
* 실행 환경의 시간대에 흔들리지 않는다. "오늘"처럼 순간을 날짜로 바꿀 때만
* 시간대가 들어간다(`todayDateKey` · E14 §10).
*/
const DATE_KEY = /^(\d{4})-(\d{2})-(\d{2})$/;
const TIME_OF_DAY = /^([01]\d|2[0-3]):([0-5]\d)$/;
const WEEKDAY_SHORT = [
	"일",
	"월",
	"화",
	"수",
	"목",
	"금",
	"토"
];
/** 월요일 시작 주의 요일 머리 — 저장소의 달력(일정 · 오늘 할 일)과 같다. */
const WEEKDAY_HEADERS_MONDAY_FIRST = [
	"월",
	"화",
	"수",
	"목",
	"금",
	"토",
	"일"
];
function toUtc(key) {
	const { year, month, day } = parseDateKey(key);
	return new Date(Date.UTC(year, month - 1, day));
}
function fromUtc(date) {
	return date.toISOString().slice(0, 10);
}
/** 실제로 있는 날짜의 `YYYY-MM-DD`인지 — `2026-02-30`은 아니다. */
function isDateKey(value) {
	const match = DATE_KEY.exec(value);
	if (!match) return false;
	const [, y, m, d] = match.map(Number);
	const date = new Date(Date.UTC(y, m - 1, d));
	return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}
function parseDateKey(key) {
	const match = DATE_KEY.exec(key);
	if (!match) throw new RangeError(`YYYY-MM-DD가 아니에요: ${key}`);
	return {
		year: Number(match[1]),
		month: Number(match[2]),
		day: Number(match[3])
	};
}
/** 넘친 월·일은 다음 달·해로 넘긴다(`Date.UTC`와 같다). */
function dateKeyOf(year, month, day) {
	return fromUtc(new Date(Date.UTC(year, month - 1, day)));
}
/** 순간이 그 시간대에서 어느 날짜인지 — 한국 시간 자정 전후로 갈린다. */
function todayDateKey(now, timeZone = "Asia/Seoul") {
	return formatLocalDate(now, timeZone);
}
function addDays(key, days) {
	const date = toUtc(key);
	date.setUTCDate(date.getUTCDate() + days);
	return fromUtc(date);
}
/** 달을 옮기고, 그 달에 없는 날(1월 31일 → 2월)은 그 달 마지막 날로 맞춘다. */
function addMonths(key, months) {
	const { year, month, day } = parseDateKey(key);
	const lastDay = new Date(Date.UTC(year, month - 1 + months + 1, 0)).getUTCDate();
	return dateKeyOf(year, month + months, Math.min(day, lastDay));
}
/** 0 = 월요일 … 6 = 일요일 */
function mondayFirstWeekday(key) {
	return (toUtc(key).getUTCDay() + 6) % 7;
}
function startOfWeekKey(key) {
	return addDays(key, -mondayFirstWeekday(key));
}
function endOfWeekKey(key) {
	return addDays(key, 6 - mondayFirstWeekday(key));
}
/** `min`·`max`(둘 다 선택) 안으로 끌어온다. 문자열 비교가 날짜 비교와 같다. */
function clampDateKey(key, min, max) {
	if (min && key < min) return min;
	if (max && key > max) return max;
	return key;
}
/** 6주 × 7일 = 42칸, 월요일 시작. 앞뒤 달 날짜로 칸을 채워 달마다 높이가 같다. */
function monthGridKeys(year, month) {
	const start = startOfWeekKey(dateKeyOf(year, month, 1));
	return Array.from({ length: 42 }, (_, i) => {
		const key = addDays(start, i);
		const parts = parseDateKey(key);
		return {
			key,
			day: parts.day,
			inMonth: parts.month === month && parts.year === year
		};
	});
}
/** `10월 3일 (토)` */
function formatDateKeyShort(key) {
	const { month, day } = parseDateKey(key);
	return `${month}월 ${day}일 (${WEEKDAY_SHORT[toUtc(key).getUTCDay()]})`;
}
/** `2026년 10월 3일 토요일` — 화면 낭독기가 읽는 날짜 이름 */
function formatDateKeyLong(key) {
	const { year, month, day } = parseDateKey(key);
	return `${year}년 ${month}월 ${day}일 ${WEEKDAY_SHORT[toUtc(key).getUTCDay()]}요일`;
}
/** `2026년 10월` */
function formatYearMonth(year, month) {
	return `${year}년 ${month}월`;
}
/** `HH:mm`(24시간)인지 */
function isTimeOfDay(value) {
	return TIME_OF_DAY.test(value);
}
/** `14:05` → `오후 2:05` */
function formatTimeOfDay(value) {
	const match = TIME_OF_DAY.exec(value);
	if (!match) throw new RangeError(`HH:mm이 아니에요: ${value}`);
	const hour = Number(match[1]);
	return `${hour < 12 ? "오전" : "오후"} ${hour % 12 === 0 ? 12 : hour % 12}:${match[2]}`;
}
/** 하루를 `stepMinutes` 간격으로 나눈 `HH:mm` 목록. `include`가 간격 밖이면 제자리에 끼운다. */
function timeOfDayOptions(stepMinutes, include) {
	if (!Number.isInteger(stepMinutes) || stepMinutes <= 0 || 1440 % stepMinutes !== 0) throw new RangeError(`하루를 나누어떨어지게 하는 분이어야 해요: ${stepMinutes}`);
	const options = [];
	for (let minutes = 0; minutes < 1440; minutes += stepMinutes) {
		const hh = String(Math.floor(minutes / 60)).padStart(2, "0");
		const mm = String(minutes % 60).padStart(2, "0");
		options.push(`${hh}:${mm}`);
	}
	if (include && isTimeOfDay(include) && !options.includes(include)) {
		options.push(include);
		options.sort();
	}
	return options;
}
//#endregion
//#region src/upcoming.ts
/**
* Upcoming 학회 마감 순수 규칙 — L3-ML-06 §4 · S13 §4 · E7 §3.3
*
* 마감은 한 학회에 여러 건이다(Abstract·제출, 라운드별 — D108). 화면·정렬·알림이
* 기준으로 삼는 것은 언제나 **지금 이후로 가장 가까운 한 건**이다.
* 이 모듈의 함수는 전부 `now`를 인자로 받는다 — 내부에서 시계를 읽지 않는다.
*/
const UPCOMING_DEADLINE_KINDS = ["abstract", "submission"];
const DEADLINE_KIND_LABEL = {
	abstract: "Abstract 마감",
	submission: "제출 마감"
};
function deadlineKindLabel(kind) {
	return DEADLINE_KIND_LABEL[kind];
}
/**
* 마감 한 건의 이름 — 캘린더 일정 제목과 `.ics`의 `SUMMARY`가 같은 규칙을 쓴다
* (E5 §9.3 · E10 §5.7). `calendar_feed` 뷰의 SQL 식도 이 규칙을 그대로 옮긴 것이고,
* `upcoming.service.int.spec.ts`가 두 값이 같은지 대조한다.
*
* `roundLabel`을 붙이는 이유는 라운드제 학회가 같은 이름의 `제출 마감`을 두 줄 만들기
* 때문이다 — 캘린더에서 구분할 수 없는 두 항목이 된다.
*/
function formatDeadlineTitle(event, deadline) {
	const name = event.abbrev ?? event.eventName;
	const round = deadline.roundLabel ? ` · ${deadline.roundLabel}` : "";
	return `${name} ${deadlineKindLabel(deadline.kind)}${round}`;
}
/**
* 카운트다운 표기와 임박 구분은 여기 없다 — `packages/ui`의 `Countdown`(D1 §7-4)이 소유한다.
* 같은 규칙을 두 곳에 두면 화면과 계산이 갈라진다.
*/
/**
* 지금 이후로 가장 가까운 마감. 전부 지났으면 null이고, 그때만 `마감됨`이다.
* 마감 시각과 같은 순간은 이미 지난 것으로 본다 — 마감은 그 시각까지다.
*/
function pickNextDeadline(deadlines, now) {
	const base = typeof now === "string" ? new Date(now) : now;
	let next = null;
	let nextMs = Number.POSITIVE_INFINITY;
	for (const deadline of deadlines) {
		const at = new Date(deadline.deadlineAt).getTime();
		if (at <= base.getTime() || at >= nextMs) continue;
		next = deadline;
		nextMs = at;
	}
	return next;
}
/**
* 사용자 시간대로 변환한 마감 시각 — `AC-ML-06-32`.
* 이 값만 보여주면 사용자가 공고와 대조할 수 없으므로, 화면은 언제나
* `UpcomingDeadline.sourceTimezone`(공고 원문 표기)과 **함께** 제시한다.
*/
function formatDeadlineForUser(deadlineAt, timeZone = "Asia/Seoul") {
	const instant = typeof deadlineAt === "string" ? new Date(deadlineAt) : deadlineAt;
	const parts = new Intl.DateTimeFormat("en-CA", {
		timeZone,
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
		hourCycle: "h23",
		timeZoneName: "shortOffset"
	}).formatToParts(instant);
	const part = (type) => parts.find((candidate) => candidate.type === type)?.value ?? "";
	return `${part("year")}-${part("month")}-${part("day")} ${part("hour")}:${part("minute")}:${part("second")} ${part("timeZoneName")}`;
}
const KO_WEEKDAYS = [
	"일",
	"월",
	"화",
	"수",
	"목",
	"금",
	"토"
];
function wallClockIn(instant, timeZone) {
	const parts = new Intl.DateTimeFormat("en-US", {
		timeZone,
		year: "numeric",
		month: "numeric",
		day: "numeric",
		hour: "numeric",
		minute: "numeric",
		hourCycle: "h23"
	}).formatToParts(instant);
	const part = (type) => Number(parts.find((candidate) => candidate.type === type)?.value ?? 0);
	const year = part("year");
	const month = part("month");
	const day = part("day");
	return {
		year,
		month,
		day,
		weekday: new Date(Date.UTC(year, month - 1, day)).getUTCDay(),
		hour: part("hour") % 24,
		minute: part("minute")
	};
}
/** `10월 22일 (목) 오후 8:59` — 해가 `nowYear`와 다르면 앞에 `2027년`을 붙인다. */
function formatWallClock(clock, nowYear) {
	const year = nowYear !== void 0 && clock.year !== nowYear ? `${clock.year}년 ` : "";
	const meridiem = clock.hour < 12 ? "오전" : "오후";
	const hour12 = clock.hour % 12 === 0 ? 12 : clock.hour % 12;
	const minute = String(clock.minute).padStart(2, "0");
	return `${year}${clock.month}월 ${clock.day}일 (${KO_WEEKDAYS[clock.weekday]}) ${meridiem} ${hour12}:${minute}`;
}
/**
* 사용자 시간대의 마감 한 줄 — `10월 22일 (목) 오후 8:59`(S13 §4 · D156 ③). 초와 오프셋은 적지 않는다.
* 공고 원문 시간대는 `formatDeadlineInSourceZone`으로 보조 글자에 함께 낸다(`AC-ML-06-32`).
*/
function formatDeadlineLocal(deadlineAt, timeZone = "Asia/Seoul", now = /* @__PURE__ */ new Date()) {
	const instant = typeof deadlineAt === "string" ? new Date(deadlineAt) : deadlineAt;
	const base = typeof now === "string" ? new Date(now) : now;
	return formatWallClock(wallClockIn(instant, timeZone), wallClockIn(base, timeZone).year);
}
/**
* 공고 시간대의 같은 마감 — `AoE (UTC-12)`처럼 표기에 UTC 오프셋이 있으면 그 시간대의 시각을, 없으면 null.
* 화면은 null이면 표기만 보인다.
*/
function formatDeadlineInSourceZone(deadlineAt, sourceTimezone, now = /* @__PURE__ */ new Date()) {
	const match = /UTC\s*([+\-−])\s*(\d{1,2})(?::?(\d{2}))?/.exec(sourceTimezone);
	if (!match) return null;
	const offsetMinutes = (match[1] === "+" ? 1 : -1) * (Number(match[2]) * 60 + Number(match[3] ?? 0));
	const instant = typeof deadlineAt === "string" ? new Date(deadlineAt) : deadlineAt;
	const shifted = new Date(instant.getTime() + offsetMinutes * 6e4);
	const base = typeof now === "string" ? new Date(now) : now;
	const nowShifted = new Date(base.getTime() + offsetMinutes * 6e4);
	return formatWallClock(wallClockIn(shifted, "UTC"), wallClockIn(nowShifted, "UTC").year);
}
const MONTHS = {
	jan: 1,
	feb: 2,
	mar: 3,
	apr: 4,
	may: 5,
	jun: 6,
	jul: 7,
	aug: 8,
	sep: 9,
	sept: 9,
	oct: 10,
	nov: 11,
	dec: 12
};
function monthOf(name) {
	const key = name.toLowerCase().replace(/\.$/, "");
	return MONTHS[key] ?? MONTHS[key.slice(0, 3)];
}
/**
* 수집원의 영문 개최 일정을 한국어 날짜로 — `July 12-18, 2027` → `2027년 7월 12일 ~ 18일`(D156 ④).
* 모르는 모양(`December 2027 (exact dates TBD)` 등)은 원문 그대로 둔다(S13 §9).
*/
function formatScheduleText(text) {
	const source = text.trim().replace(/[–—]/g, "-");
	const MONTH = "([A-Za-z]{3,9}\\.?)";
	const sameMonth = new RegExp(`^${MONTH}\\s+(\\d{1,2})\\s*-\\s*(\\d{1,2}),?\\s+(\\d{4})$`).exec(source);
	if (sameMonth) {
		const month = monthOf(sameMonth[1]);
		if (month) return `${sameMonth[4]}년 ${month}월 ${sameMonth[2]}일 ~ ${sameMonth[3]}일`;
	}
	const twoMonths = new RegExp(`^${MONTH}\\s+(\\d{1,2})\\s*-\\s*${MONTH}\\s+(\\d{1,2}),?\\s+(\\d{4})$`).exec(source);
	if (twoMonths) {
		const from = monthOf(twoMonths[1]);
		const to = monthOf(twoMonths[3]);
		if (from && to) return `${twoMonths[5]}년 ${from}월 ${twoMonths[2]}일 ~ ${to}월 ${twoMonths[4]}일`;
	}
	const twoYears = new RegExp(`^${MONTH}\\s+(\\d{1,2}),?\\s+(\\d{4})\\s*-\\s*${MONTH}\\s+(\\d{1,2}),?\\s+(\\d{4})$`).exec(source);
	if (twoYears) {
		const from = monthOf(twoYears[1]);
		const to = monthOf(twoYears[4]);
		if (from && to) return `${twoYears[3]}년 ${from}월 ${twoYears[2]}일 ~ ${twoYears[6]}년 ${to}월 ${twoYears[5]}일`;
	}
	const single = new RegExp(`^${MONTH}\\s+(\\d{1,2}),?\\s+(\\d{4})$`).exec(source);
	if (single) {
		const month = monthOf(single[1]);
		if (month) return `${single[3]}년 ${month}월 ${single[2]}일`;
	}
	const dayFirst = new RegExp(`^(\\d{1,2})\\s*-\\s*(\\d{1,2})\\s+${MONTH},?\\s+(\\d{4})$`).exec(source);
	if (dayFirst) {
		const month = monthOf(dayFirst[3]);
		if (month) return `${dayFirst[4]}년 ${month}월 ${dayFirst[1]}일 ~ ${dayFirst[2]}일`;
	}
	const monthOnly = new RegExp(`^${MONTH}\\s+(\\d{4})$`).exec(source);
	if (monthOnly) {
		const month = monthOf(monthOnly[1]);
		if (month) return `${monthOnly[2]}년 ${month}월`;
	}
	return text;
}
//#endregion
//#region src/calendar-export.ts
/**
* 외부 캘린더 서비스로 마감 하나를 보내는 작성 링크 — S13 §8.1 · `AC-ML-06-14`
*
* 표준 캘린더 파일(`.ics`)은 서버가 만든다(E10 §5.7). 세 서비스는 각자의 일정 작성 화면
* URL이라 클라이언트가 조립한다 — 서버가 리다이렉트를 대신 열면 팝업 차단을 구분할 수
* 없어 `AC-ML-06-15`의 실패 안내를 만들지 못한다.
*
* 세 링크 모두 **이벤트 하나만** 싣는다. 여러 마감을 한 번에 넣는 것은 `.ics`가 맡는다.
*/
const CALENDAR_SERVICES = [
	"google",
	"outlook",
	"yahoo"
];
const CALENDAR_SERVICE_LABEL = {
	google: "Google 캘린더",
	outlook: "Outlook",
	yahoo: "Yahoo 캘린더"
};
/** `20260730T115959Z` — Google·Yahoo가 받는 basic 형식 */
function toBasicUtcStamp(instant) {
	return `${(typeof instant === "string" ? new Date(instant) : instant).toISOString().replace(/[-:]/g, "").slice(0, 15)}Z`;
}
function buildCalendarServiceUrl(service, event) {
	const stamp = toBasicUtcStamp(event.deadlineAt);
	const iso = new Date(event.deadlineAt).toISOString();
	switch (service) {
		case "google": return `https://calendar.google.com/calendar/render?${new URLSearchParams({
			action: "TEMPLATE",
			text: event.title,
			dates: `${stamp}/${stamp}`,
			details: event.description
		}).toString()}`;
		case "outlook": return `https://outlook.office.com/calendar/0/deeplink/compose?${new URLSearchParams({
			path: "/calendar/action/compose",
			rru: "addevent",
			subject: event.title,
			startdt: iso,
			enddt: iso,
			body: event.description
		}).toString()}`;
		case "yahoo": return `https://calendar.yahoo.com/?${new URLSearchParams({
			v: "60",
			title: event.title,
			st: stamp,
			et: stamp,
			desc: event.description
		}).toString()}`;
	}
}
/**
* **제목은 검증 상태와 무관하다** — S13 §8.3 `S13-Q6`. 제목에 확인 전 표시를 붙이면 사용자의
* 개인 캘린더에 우리 검증 상태가 영구히 박힌다: 내보낸 뒤 검증이 끝나도 그 일정 제목은 그대로
* 남고 사용자가 직접 고쳐야 한다. 확인 전이라는 사실은 `formatExportDescription`이 말한다.
*/
function formatExportSummary(event, deadline) {
	return formatDeadlineTitle(event, deadline);
}
/** 사용자가 공고와 대조할 수 있어야 한다 — `AC-ML-06-32` · `AC-ML-06-24` */
function formatExportDescription(event, deadline) {
	const lines = [event.eventName, `공고 기준 시간대: ${deadline.sourceTimezone}`];
	if (event.officialUrl) lines.push(`공식 홈페이지: ${event.officialUrl}`);
	if (!event.verified) lines.push("공식 홈페이지에서 아직 확인되지 않은 마감입니다.");
	return lines.join("\n");
}
//#endregion
//#region src/citation.ts
/**
* 인용 형식 — E6 §7.1 · D137 ②
*
* BibTeX·RIS는 문법이 확정적이라 여기서 만든다. APA·IEEE는 서버가 citeproc-js + CSL 스타일로
* 만든다 — 스타일 규칙을 손으로 옮기지 않고, citeproc이 클라이언트 번들에 들어가지 않게 한다.
* 그 입력인 CSL-JSON 매핑(`toCslJson`)만 여기에 둔다.
*/
const CITATION_FORMATS = [
	"bibtex",
	"ris",
	"apa",
	"ieee"
];
function isDeterministicCitationFormat(format) {
	return format === "bibtex" || format === "ris";
}
/** 값 안의 개행이 RIS 줄이나 BibTeX 필드를 끊지 않게 한 줄로 만든다 */
function oneLine(value) {
	return value.replace(/\s+/g, " ").trim();
}
function present(value) {
	if (value == null) return null;
	const line = oneLine(value);
	return line === "" ? null : line;
}
/** 쉼표가 있으면 `성, 이름`, 없으면 마지막 어절을 성으로 본다(OpenAlex 표기) */
function parseAuthorName(raw) {
	const s = oneLine(raw);
	const comma = s.indexOf(",");
	if (comma !== -1) {
		const family = s.slice(0, comma).trim();
		const given = s.slice(comma + 1).trim();
		return given === "" ? { family } : {
			family,
			given
		};
	}
	const parts = s.split(" ");
	if (parts.length === 1) return { family: s };
	return {
		family: parts.at(-1),
		given: parts.slice(0, -1).join(" ")
	};
}
function pageRange(src, separator) {
	const first = present(src.firstPage);
	if (!first) return null;
	const last = present(src.lastPage);
	return last ? `${first}${separator}${last}` : first;
}
function toCslJson(src, id = "item-1") {
	const item = {
		id,
		type: "article-journal",
		author: src.authors.map(parseAuthorName).filter((name) => name.family !== "")
	};
	const title = present(src.title);
	if (title) item.title = title;
	const venue = present(src.venue);
	if (venue) item["container-title"] = venue;
	if (src.year) item.issued = { "date-parts": [[src.year]] };
	const volume = present(src.volume);
	if (volume) item.volume = volume;
	const issue = present(src.issue);
	if (issue) item.issue = issue;
	const pages = pageRange(src, "-");
	if (pages) item.page = pages;
	const doi = present(src.doi);
	if (doi) item.DOI = doi;
	const url = present(src.url);
	if (url) item.URL = url;
	return item;
}
const BIBTEX_TEXT_ESCAPES = {
	"\\": "\\textbackslash{}",
	"{": "\\textbraceleft{}",
	"}": "\\textbraceright{}",
	"~": "\\textasciitilde{}",
	"^": "\\textasciicircum{}"
};
/** 중괄호는 백슬래시가 앞에 있어도 BibTeX가 세므로 명령으로 바꾼다 — 값이 필드를 닫지 못한다 */
function bibtexEscape(s) {
	return s.replace(/[\\{}~^$&%#_]/g, (ch) => BIBTEX_TEXT_ESCAPES[ch] ?? `\\${ch}`);
}
/** `doi`·`url`은 그대로 읽히는 필드라 필드를 깨는 문자만 뺀다 */
function bibtexVerbatim(s) {
	return s.replace(/[{}\\\s]/g, "");
}
/** `kim2026solidstate` — 첫 저자 성 + 연도 + 제목에서 글자만 남겨 3자 이상인 첫 단어. ASCII만 남긴다 */
function bibtexKey(src) {
	const firstAuthor = src.authors[0];
	const family = firstAuthor ? parseAuthorName(firstAuthor).family : "";
	const word = (present(src.title) ?? "").split(" ").map((w) => w.replace(/[^\p{L}]/gu, "")).find((w) => w.length >= 3) ?? "";
	const key = `${family}${src.year ?? ""}${word}`.toLowerCase().replace(/[^a-z0-9]/g, "");
	return key === "" ? "item" : key;
}
function toBibtex(src) {
	const fields = [];
	const authors = src.authors.map(present).filter((a) => a !== null);
	if (authors.length > 0) fields.push(["author", authors.map(bibtexEscape).join(" and ")]);
	const title = present(src.title);
	if (title) fields.push(["title", bibtexEscape(title)]);
	const venue = present(src.venue);
	if (venue) fields.push(["journal", bibtexEscape(venue)]);
	if (src.year) fields.push(["year", String(src.year)]);
	const volume = present(src.volume);
	if (volume) fields.push(["volume", bibtexEscape(volume)]);
	const issue = present(src.issue);
	if (issue) fields.push(["number", bibtexEscape(issue)]);
	const pages = pageRange(src, "--");
	if (pages) fields.push(["pages", bibtexEscape(pages)]);
	const doi = present(src.doi);
	if (doi) fields.push(["doi", bibtexVerbatim(doi)]);
	const url = present(src.url);
	if (url) fields.push(["url", bibtexVerbatim(url)]);
	const locator = present(src.locator);
	if (locator) fields.push(["note", bibtexEscape(locator)]);
	const body = fields.map(([k, v]) => `  ${k} = {${v}}`).join(",\n");
	return `@article{${bibtexKey(src)},\n${body}\n}`;
}
function toRis(src) {
	const lines = ["TY  - JOUR"];
	for (const author of src.authors) {
		const name = present(author);
		if (name) lines.push(`AU  - ${name}`);
	}
	const push = (tag, value) => {
		if (value) lines.push(`${tag}  - ${value}`);
	};
	push("TI", present(src.title));
	push("JO", present(src.venue));
	push("PY", src.year ? String(src.year) : null);
	push("VL", present(src.volume));
	push("IS", present(src.issue));
	push("SP", present(src.firstPage));
	push("EP", present(src.lastPage));
	push("DO", present(src.doi));
	push("UR", present(src.url));
	push("N1", present(src.locator));
	lines.push("ER  - ");
	return lines.join("\n");
}
function formatDeterministicCitation(src, format) {
	return format === "bibtex" ? toBibtex(src) : toRis(src);
}
//#endregion
//#region src/domain.ts
/**
* 도메인 공통 상수 — web / api가 함께 참조한다.
* DB 스키마 타입은 여기 두지 않는다(apps/api 전용, L2-06 §9 성과 정보 공개 범위).
*/
/** 최상위 뷰. 정확히 하나만 표시된다 — L3-PF-01 / AC-PF-01-01 */
const TOP_LEVEL_VIEWS = [
	"landing",
	"mylab",
	"funding",
	"community",
	"flow"
];
/** 랩 내 역할 — L2-06 §5 권고 모델 */
const MEMBERSHIP_ROLES = ["pi", "member"];
/** 온보딩에서 분류되는 연구 자산 종류 — L3-ON-02 */
const RESEARCH_ASSET_KINDS = [
	"paper",
	"patent",
	"certificate",
	"project",
	"slide"
];
const UPLOAD_ACCEPTED_MIME = "application/pdf";
const UPLOAD_MAX_FILE_BYTES = 52428800;
/** 비로그인 접근 정책 — L3-PF-01 / AC-PF-01-02 */
const PUBLIC_VIEWS = ["landing", "community"];
function requiresAuth(view) {
	return !PUBLIC_VIEWS.includes(view);
}
//#endregion
//#region src/normalize.ts
/**
* 인용 문장 정규화 — E6 §2.2 · §5 · §6. 2MB 외부 본문에도 쓰이므로 되짚는 정규식을 두지 않는다.
*/
/** 하이픈 · 유니코드 하이픈 · 줄바꿈 금지 하이픈 · 소프트 하이픈. 대시(–—)는 결합하지 않는다 */
const LINE_BREAK_HYPHEN = /[-\u2010\u2011\u00ad]\s+/g;
const WORD_CHAR = /^[\p{L}\p{N}_]$/u;
const LIGATURES = [
	["ﬀ", "ff"],
	["ﬁ", "fi"],
	["ﬂ", "fl"],
	["ﬃ", "ffi"],
	["ﬄ", "ffl"]
];
function codePointBefore(s, index) {
	if (index <= 0) return void 0;
	const low = s.charCodeAt(index - 1);
	const start = low >= 56320 && low <= 57343 && index >= 2 ? index - 2 : index - 1;
	return String.fromCodePoint(s.codePointAt(start));
}
function codePointAt(s, index) {
	const code = s.codePointAt(index);
	return code === void 0 ? void 0 : String.fromCodePoint(code);
}
function isWordChar(ch) {
	return ch !== void 0 && WORD_CHAR.test(ch);
}
/** `electro-\nlyte` → `electrolyte`. 개행이 없는 `solid- state`는 그대로 둔다 */
function joinLineBreakHyphens(s) {
	return s.replace(LINE_BREAK_HYPHEN, (match, offset, whole) => {
		if (!match.includes("\n")) return match;
		return isWordChar(codePointBefore(whole, offset)) && isWordChar(codePointAt(whole, offset + match.length)) ? "" : match;
	});
}
function collapseSpaces(s) {
	return s.replace(/\s+/g, " ").trim();
}
/**
* E6 §2.2 ①~④ — 줄바꿈 하이픈 결합 → 리가처 복원 → NFKC → 공백 축약.
* 대소문자와 구두점은 보존한다. 앵커의 문맥·오프셋은 이 결과 위에서 잡는다.
*/
function normalizePdfText(raw) {
	let s = joinLineBreakHyphens(raw);
	for (const [from, to] of LIGATURES) s = s.replaceAll(from, to);
	return collapseSpaces(s.normalize("NFKC"));
}
/**
* `highlights.exact_normalized` — §2.2 결과를 소문자로 접고 구두점(`\p{P}`)을 공백으로 본다.
* 기호(`<` `>` `+` `°`)는 뜻을 바꾸므로 남긴다. 한 번 더 적용해도 결과가 같다.
*/
function normalizeQuote(raw) {
	return collapseSpaces(normalizePdfText(raw).toLowerCase().normalize("NFKC").replace(/\p{P}+/gu, " "));
}
/**
* 두 `normalizeQuote` 키의 관계 — E6 §5 ①·② · E10 §5.4 `match_kind`.
* 포함은 단어 경계에서만 본다(`pore`가 `spore` 안에 있다고 보지 않는다). 방향은 가리지 않는다.
*/
function matchQuoteKeys(candidate, existing) {
	if (candidate === "" || existing === "") return null;
	if (candidate === existing) return "exact";
	const a = ` ${candidate} `;
	const b = ` ${existing} `;
	return a.includes(b) || b.includes(a) ? "contains" : null;
}
/** PostgreSQL `char_length`와 같은 단위(코드 포인트)로 센다 */
function charLength(s) {
	return [...s].length;
}
//#endregion
//#region src/pdf.ts
async function extractPdfText(data) {
	const result = await extractText(data, { mergePages: true });
	return {
		text: result.text.replace(/\s+/g, " ").trim(),
		pageCount: result.totalPages
	};
}
function classifyDocumentText(text) {
	const normalized = text.normalize("NFKC").replace(/\s+/g, " ").toLowerCase();
	const patentNumber = /\b10-\d{4}-\d{6,7}\b/.test(normalized);
	const patentStructure = hasAll(normalized, [/(청구항|claims?)/, /(발명자|inventor|출원인|applicant)/]);
	if (patentNumber || patentStructure) return {
		kind: "patent",
		confidence: patentNumber ? .98 : .85
	};
	const doi = /\b10\.\d{4,9}\/[\w.()/:;-]+/i.test(normalized);
	const paperStructure = hasAll(normalized, [/(abstract|초록)/, /(references|참고문헌)/]);
	if (doi || paperStructure) return {
		kind: "paper",
		confidence: doi ? .95 : .82
	};
	const certificateName = /(이수증|수료증|인증서|certificate of|certification)/.test(normalized);
	const certificateStructure = /(발급기관|issued by|issuer|발급일|date of issue)/.test(normalized);
	if (certificateName && certificateStructure) return {
		kind: "certificate",
		confidence: .86
	};
	return {
		kind: "other",
		confidence: 0
	};
}
function hasAll(text, patterns) {
	return patterns.every((pattern) => pattern.test(text));
}
//#endregion
//#region src/polling.ts
/**
* 오래 걸리는 처리를 폴링하는 규칙 — E14 §9 / E13 §9.
*
* 여기 둔 것은 클라이언트 프레임워크와 무관한 순수 계산뿐이다. `refetchInterval`에 얼마나
* 자주 부를지, 언제 상한에 걸리는지는 이 모듈이 정하고, 언제 멈출지(종결 상태 도달)와
* 탭 이탈 처리는 각 화면이 자기 도메인 상태를 보고 결정한다.
*/
/** 첫 폴링 간격 */
const POLL_START_MS = 2e3;
/** 폴링 간격의 상한 */
const POLL_MAX_MS = 3e4;
/** 이만큼 지나면 폴링을 멈추고 "처리가 계속되고 있다"를 안내한다. 실패로 단정하지 않는다 */
const POLL_LIMIT_MS = 6e5;
/** 2초에서 시작해 30초까지 두 배씩 늘린다. `attempt`는 이번 폴링에서 이미 부른 횟수다 */
function pollDelay(attempt) {
	return Math.min(POLL_START_MS * 2 ** attempt, POLL_MAX_MS);
}
/**
* 요청 횟수 대신 경과 시간(ms)으로 간격을 정할 때 쓴다 — 작업 생성 시각만 아는 자리(배치
* job의 `createdAt` 등)에서 `pollDelay`와 같은 모양의 백오프를 재현한다. 분 단위로 한 단계씩
* 올린다.
*/
function pollDelayForElapsed(elapsedMs) {
	return pollDelay(Math.floor(elapsedMs / 6e4));
}
/** 경과 시간이 상한을 넘었는가 — 넘었으면 폴링을 멈추고 안내로 바꾼다 */
function isPollExpired(elapsedMs) {
	return elapsedMs > POLL_LIMIT_MS;
}
//#endregion
//#region src/recommendation-rerank.ts
const DEFAULT_HIGH_SIMILARITY = .95;
const DEFAULT_MODERATE_SIMILARITY = .9;
/** near_duplicate 게이트 — D14 층2. cos≥임계값 AND 같은 제1/교신저자 AND 같은 Venue (E4 §8.1) */
function isNearDuplicate(f, { highSimilarity = DEFAULT_HIGH_SIMILARITY } = {}) {
	return f.embeddingCosine >= highSimilarity && f.sameFirstOrCorrespondingAuthor && f.sameVenue;
}
/**
* 감점표 — E4 §5.1. 조건들은 배타적이지 않으므로(엑셀도 배타를 가정하지 않는다) 전부 더한다.
* 단 임베딩 유사도 두 구간은 같은 축이라 하나만 적용된다. 경계값은 R3 C4 실측 전 D103 임시값
* (E1 §5.5, `similarity_thresholds` scope='rerank_duplicate')이며 호출자가 넘긴다.
*/
function pairPenalty(f, { highSimilarity = DEFAULT_HIGH_SIMILARITY, moderateSimilarity = DEFAULT_MODERATE_SIMILARITY } = {}) {
	let penalty = 0;
	if (f.embeddingCosine >= highSimilarity) penalty += 20;
	else if (f.embeddingCosine >= moderateSimilarity) penalty += 10;
	if (f.sameFirstOrCorrespondingAuthor) penalty += 8;
	if (f.sameVenue) penalty += 3;
	if (f.keywordOverlapAtLeast80Percent) penalty += 6;
	if (f.sameTopicOnly) penalty += 4;
	return penalty;
}
/** 보너스 — E4 §5.1·§5.4. "다른 상위 키워드 충족"만 구현한다(위 모듈 설명 참고) */
function bonus({ candidateKeywordIds, userTopKeywordIds, selectedKeywordIds }) {
	return userTopKeywordIds.some((id) => !selectedKeywordIds.has(id) && candidateKeywordIds.has(id)) ? 3 : 0;
}
/**
* hop 1 가점 — R7 §3.3. 강한 인접(`strength=2`)이면서 감쇠 대상이 아닐 때만 최고 등급이다.
*
* R7 §3.3은 이 값을 코드 상수가 아니라 `ranking_weight_profiles`에 두라고 규정하지만, 그 테이블은
* `CHECK (cardinality(weights) = 5)`로 고정된 5개 배열이라 이 두 값을 얹을 자리가 없다(hop 2 허브
* 집합과 같은 문제). 저장소 확장은 #328로 추적하고, 그때까지는 이 잠정 상수를 쓴다.
*/
const HOP1_STRONG_BONUS = 3;
/** 약한 인접(`strength=1`) 또는 도착 분야 질량 감쇠(`massDamped`) 중 하나라도 해당하면 이 등급.
* 둘 다 해당해도 한 번만 깎는다(R7 §3.3 "곱이 아니라 min") — 이 tier 함수가 그 규칙을 구현한다 */
const HOP1_WEAK_OR_DAMPED_BONUS = 1.5;
/**
* 인접분야 가점 — E4 §6.3 · D19 · R7 §3.2·§3.3. **More for You 전용**이며 Daily Pick 호출부는
* `rerank()`에 `additionalBonus`를 넘기지 않아 적용되지 않는다.
*
* hop 0(사용자 자신의 세부분야)은 이미 Keyword·Embedding 점수가 반영하므로 가점이 없다.
* hop 2(간접 인접)는 구현하지 않는다 — R7 §3.5가 규정한 허브 경유 금지 규칙은 시드마다 달라지는
* 허브 집합을 설정으로 보관해야 하는데(`ranking_weight_profiles.weights`는 5개 고정 배열이라
* 이런 파라미터를 얹을 자리가 없다), 그 저장소를 새로 만드는 것은 이 PR 범위를 벗어난다.
* R7 §3.5가 명시적으로 남긴 대안인 "hop 2를 버리고 hop 0/1/무관 3단으로 축소"를 택했다.
*/
function adjacentSubfieldBonus(candidateSubfieldIds, ownSubfieldIds, adjacency) {
	if (candidateSubfieldIds.some((id) => ownSubfieldIds.has(id))) return 0;
	let best = 0;
	for (const ownId of ownSubfieldIds) {
		const edges = adjacency.get(ownId);
		if (!edges) continue;
		for (const edge of edges) {
			if (!candidateSubfieldIds.includes(edge.adjacentId)) continue;
			const value = edge.strength === 2 && !edge.massDamped ? HOP1_STRONG_BONUS : HOP1_WEAK_OR_DAMPED_BONUS;
			if (value > best) best = value;
		}
	}
	return best;
}
/**
* MMR 재순위 — E4 §5.2·§5.3 의사코드를 그대로 옮긴다. 매 스텝 이미 선정된 집합 대비
* **최대 감점**(`max` 집계, 엑셀 명시)을 적용해 다음 1위를 고른다.
*
* `pool`은 선정된 후보만 제거하고 그대로 둔다 — near_duplicate로 이번 스텝에 밀린 후보도
* 다음 스텝에서 (선정 집합이 달라졌으므로) 다시 평가된다. 끝까지 근접중복으로 남으면
* `deferred`에 담긴다.
*/
function rerank(candidates, { slots, userTopKeywordIds, pairFeatures, thresholds, additionalBonus }) {
	const pool = candidates.toSorted((a, b) => b.baseScore - a.baseScore);
	const selected = [];
	const deferred = /* @__PURE__ */ new Map();
	const evaluations = /* @__PURE__ */ new Map();
	while (selected.length < slots && pool.length > 0) {
		let best = null;
		for (const c of pool) {
			if (selected.some((s) => s.versionGroupId === c.versionGroupId)) continue;
			if (selected.some((s) => isNearDuplicate(pairFeatures(c, s), thresholds))) {
				deferred.set(c.id, c);
				continue;
			}
			const penalty = selected.reduce((max, s) => Math.max(max, pairPenalty(pairFeatures(c, s), thresholds)), 0);
			const selectedKeywordIds = /* @__PURE__ */ new Set();
			for (const s of selected) for (const kw of s.keywordIds) selectedKeywordIds.add(kw);
			const b = bonus({
				candidateKeywordIds: c.keywordIds,
				userTopKeywordIds,
				selectedKeywordIds
			});
			const extra = additionalBonus ? additionalBonus(c) : 0;
			const rerankScore = c.baseScore - penalty + b + extra;
			evaluations.set(c.id, {
				penalty,
				bonus: b + extra,
				rerankScore
			});
			if (best === null || rerankScore > best.rerankScore) best = {
				candidate: c,
				rerankScore
			};
		}
		if (best === null) break;
		selected.push(best.candidate);
		deferred.delete(best.candidate.id);
		pool.splice(pool.findIndex((c) => c.id === best.candidate.id), 1);
	}
	return {
		selected: selected.map((c) => c.item),
		evaluations,
		deferred: [...deferred.values()].map((c) => c.item)
	};
}
//#endregion
//#region src/recommendation-scoring.ts
/**
* 추천 하위 점수 계산 — E4 §2~§3 (L3-ML-05).
*
* 가중치·임계값은 인자로만 받는다. `similarity_thresholds`·`recommendation_runs.scoring_weights`
* 등 설정 테이블 값을 코드 상수로 박지 않는다는 요구 때문이다(E4 §2.1, E9 §3). 기본값은
* R3 캘리브레이션 전 Excel 원안 값(D103)이며, 실측 뒤에는 호출자가 실제값을 넘긴다.
*
* Quality Score(20점)는 별도 함수가 없다 — venue_quality_scores에 이미 13~20 스케일로
* 저장된 값을 그대로 쓴다(E3 §8.3). 여기서 재계산할 산식이 없다.
*/
function clamp01(x) {
	return Math.min(1, Math.max(0, x));
}
/** Keyword Score — E4 §3.2 확정 산식. `maxPoints` 기본 40(엑셀 원안, MVP 전 가중치) */
function keywordScore(matches, maxPoints = 40) {
	return maxPoints * matches.reduce((total, m) => total + m.weight * m.strength, 0);
}
/** Embedding Score — E4 §3.3. `cos_min`/`cos_max` 없이 `30 × cos`를 쓰면 안 된다(§3.3 경고) */
function embeddingScore({ cosine, cosMin, cosMax, maxPoints = 30, isFallbackVector = false, fallbackPenalty = .7 }) {
	const score = maxPoints * (cosMax === cosMin ? 0 : clamp01((cosine - cosMin) / (cosMax - cosMin)));
	return isFallbackVector ? score * fallbackPenalty : score;
}
/**
* 1년 수집 범위 내부 선형 보간 앵커 — E4 §3.5. 수집 범위가 넓어지면 뒤쪽 앵커가
* 엑셀 원표 그대로 자동 승계된다. `[days, score]` 오름차순.
*/
const FRESHNESS_ANCHORS = [
	[0, 10],
	[30, 10],
	[90, 9],
	[180, 8],
	[270, 7],
	[365, 6],
	[730, 5],
	[1095, 4],
	[1825, 2],
	[3650, 1]
];
/**
* Freshness Score — E4 §3.5. `ageDays`는 호출자가 배치 기준 시각(`recommendation_runs.started_at`)
* 으로 계산해 넘긴다 — 이 함수는 시계를 읽지 않는다.
*/
function freshnessScore(ageDays, anchors = FRESHNESS_ANCHORS) {
	const first = anchors[0];
	if (!first) throw new Error("freshnessScore: anchors must not be empty");
	if (ageDays <= 0) return first[1];
	let prev = first;
	for (const anchor of anchors.slice(1)) {
		const [d1, s1] = anchor;
		const [d0, s0] = prev;
		if (ageDays <= d1) return s0 + (ageDays - d0) / (d1 - d0) * (s1 - s0);
		prev = anchor;
	}
	return prev[1];
}
/** 기본점수 = 4개 하위 점수의 합 — E4 §2.2·§2.3 (예시 점수값 자체는 재현 대상이 아니다, D16) */
function baseScore({ keyword, embedding, quality, freshness }) {
	return keyword + embedding + quality + freshness;
}
//#endregion
//#region src/selectors.ts
/**
* 인용 문장 앵커 — E6 §2.1 · §3.1 · §7.2. 문맥·오프셋은 `normalizePdfText(원문)`의 `String#slice` 인덱스 기준이다.
*/
/** 앞뒤 문맥 상한 — `prefix_text`/`suffix_text` · 확장 요청 한도 (E6 §3.2) */
const HIGHLIGHT_CONTEXT_MAX_LENGTH = 32;
/** 인용문 상한 — `CHECK (char_length(exact_text) <= 1000)` (E6 §2.3) */
const HIGHLIGHT_EXACT_MAX_LENGTH = 1e3;
/** 위치 자유 입력 상한 — `char_length(locator) <= 64` (E6 §2.3) */
const HIGHLIGHT_LOCATOR_MAX_LENGTH = 64;
function isWithinContextLimit(context) {
	return context == null || charLength(context) <= 32;
}
function isLowSurrogate(code) {
	return code >= 56320 && code <= 57343;
}
function isHighSurrogate(code) {
	return code >= 55296 && code <= 56319;
}
/** `start` 앞의 코드 포인트 `n`개. 서로게이트 쌍을 가르지 않는다 */
function takeBefore(text, start, n) {
	let i = start;
	for (let count = 0; i > 0 && count < n; count += 1) {
		const pair = i >= 2 && isLowSurrogate(text.charCodeAt(i - 1)) && isHighSurrogate(text.charCodeAt(i - 2));
		i -= pair ? 2 : 1;
	}
	return text.slice(i, start);
}
function takeAfter(text, end, n) {
	let i = end;
	for (let count = 0; i < text.length && count < n; count += 1) {
		const pair = i + 1 < text.length && isHighSurrogate(text.charCodeAt(i)) && isLowSurrogate(text.charCodeAt(i + 1));
		i += pair ? 2 : 1;
	}
	return text.slice(end, i);
}
function contextAt(text, start, end, contextLength) {
	return {
		prefix: takeBefore(text, start, contextLength) || null,
		suffix: takeAfter(text, end, contextLength) || null
	};
}
/**
* 원문에서 고른 범위로 TextQuote를 만든다 — 확장이 읽던 페이지에서 쓴다.
* `text`와 오프셋은 같은 문자열 기준이며, 문맥은 상한을 넘지 않는다.
*/
function quoteSelectorFromRange(text, start, end, contextLength = 32) {
	if (start < 0 || end > text.length || start >= end) throw new RangeError(`invalid selection range: ${start}..${end} of ${text.length}`);
	return {
		exact: text.slice(start, end),
		...contextAt(text, start, end, contextLength)
	};
}
/** 조건을 만족하는 자리를 두 개까지만 찾는다 — 셋째부터는 판정을 바꾸지 않는다 */
function firstTwoOccurrences(text, needle, accept) {
	const found = [];
	for (let at = text.indexOf(needle); at !== -1 && found.length < 2; at = text.indexOf(needle, at + 1)) if (accept(at)) found.push(at);
	return found;
}
function contextMatches(text, start, end, prefix, suffix) {
	const before = text.slice(Math.max(0, start - prefix.length - 1), start).trimEnd();
	const after = text.slice(end, end + suffix.length + 1).trimStart();
	return before.endsWith(prefix) && after.startsWith(suffix);
}
/**
* 원문에서 인용문 자리를 찾는다 — 저장 시 앵커 잡기(E6 §3.1)와 재탐색(§7.2.2)이 함께 쓴다.
*
*   ① 저장된 오프셋에 인용문이 그대로 있다          → `position`
*   ② 인용문이 원문에 정확히 한 번 나온다           → `quote`
*   ③ 여러 번 나오면 저장된 문맥이 하나로 가른다    → `context`
*   그 밖에는 찾지 못한 것이다. 여러 곳 중 하나를 고르지 않는다.
*/
function locateQuote(document, selectors, contextLength = 32) {
	const text = normalizePdfText(document);
	const needle = normalizePdfText(selectors.quote.exact);
	if (needle === "") return {
		found: false,
		reason: "absent"
	};
	const located = (via, start) => {
		const end = start + needle.length;
		return {
			found: true,
			via,
			start,
			end,
			...contextAt(text, start, end, contextLength)
		};
	};
	const prefix = normalizePdfText(selectors.quote.prefix ?? "");
	const suffix = normalizePdfText(selectors.quote.suffix ?? "");
	const hasContext = prefix !== "" || suffix !== "";
	const fitsContext = (at) => contextMatches(text, at, at + needle.length, prefix, suffix);
	const hits = firstTwoOccurrences(text, needle, () => true);
	if (hits.length === 0) return {
		found: false,
		reason: "absent"
	};
	const stored = selectors.position?.start;
	if (stored !== void 0 && stored >= 0 && text.startsWith(needle, stored) && (hits.length === 1 || !hasContext || fitsContext(stored))) return located("position", stored);
	if (hits.length === 1) return located("quote", hits[0]);
	if (!hasContext) return {
		found: false,
		reason: "ambiguous"
	};
	const narrowed = firstTwoOccurrences(text, needle, fitsContext);
	return narrowed.length === 1 ? located("context", narrowed[0]) : {
		found: false,
		reason: "ambiguous"
	};
}
//#endregion
//#region src/vector.ts
/**
* 벡터 연산 — E9 §4.3(`vector` 모듈: L2 정규화·가중합·코사인), E2 §5.3(프로필 벡터 산식).
*
* 순수 함수만 둔다. 어떤 벡터를 조회하고 어디에 저장할지는 호출자(워커) 책임이다.
*/
function magnitude(v) {
	let sumSquares = 0;
	for (const x of v) sumSquares += x * x;
	return Math.sqrt(sumSquares);
}
/** 길이 0 벡터는 그대로 반환한다(정규화할 방향이 없다) */
function l2Normalize(v) {
	const mag = magnitude(v);
	if (mag === 0) return [...v];
	return v.map((x) => x / mag);
}
/** Σ wᵢ · vᵢ. 벡터 차원이 다르면 던진다 — 조용히 잘린 벡터를 섞으면 안 된다 */
function weightedSum(vectors) {
	if (vectors.length === 0) return [];
	const dimensions = vectors[0].vector.length;
	for (const { vector } of vectors) if (vector.length !== dimensions) throw new Error(`weightedSum: 벡터 차원이 일치하지 않는다 (${dimensions} vs ${vector.length})`);
	return Array.from({ length: dimensions }, (_, i) => vectors.reduce((sum, { vector, weight }) => sum + (vector[i] ?? 0) * weight, 0));
}
/** 코사인 유사도. 둘 중 하나라도 영벡터면 0을 반환한다(정의역 밖) */
function cosineSimilarity(a, b) {
	if (a.length !== b.length) throw new Error(`cosineSimilarity: 벡터 차원이 일치하지 않는다 (${a.length} vs ${b.length})`);
	const magA = magnitude(a);
	const magB = magnitude(b);
	if (magA === 0 || magB === 0) return 0;
	let dot = 0;
	for (let i = 0; i < a.length; i++) dot += (a[i] ?? 0) * (b[i] ?? 0);
	return dot / (magA * magB);
}
/**
* Topic-first 프로필 벡터 — E2 §5.3: `p̂ = normalize(Σ wᵢ · normalize(topic_embeddingᵢ))`.
* 각 Topic 임베딩을 먼저 정규화하는 이유: 정규화 없이 가중합하면 Topic 간 벡터 크기 차이가
* 관심 강도로 오인되고, 최종 정규화 없이 두면 확정한 Topic 수에 따라 벡터 크기가 달라져
* 절대 임계값(코사인 기반 점수·컷오프)이 불공정해진다.
*/
function buildProfileVector(topics) {
	return l2Normalize(weightedSum(topics.map((t) => ({
		vector: l2Normalize(t.embedding),
		weight: t.weight
	}))));
}
//#endregion
export { CALENDAR_SERVICES, CALENDAR_SERVICE_LABEL, CITATION_FORMATS, EVIDENCE_TYPES, FRESHNESS_ANCHORS, HIGHLIGHT_CONTEXT_MAX_LENGTH, HIGHLIGHT_EXACT_MAX_LENGTH, HIGHLIGHT_LOCATOR_MAX_LENGTH, MEMBERSHIP_ROLES, POLL_LIMIT_MS, POLL_MAX_MS, POLL_START_MS, PUBLIC_VIEWS, RESEARCH_ASSET_KINDS, TOP_LEVEL_VIEWS, UPCOMING_DEADLINE_KINDS, UPLOAD_ACCEPTED_MIME, UPLOAD_MAX_FILE_BYTES, WEEKDAY_HEADERS_MONDAY_FIRST, addDays, addMonths, adjacentSubfieldBonus, assertHasEvidence, baseScore, bibtexKey, bonus, buildCalendarServiceUrl, buildProfileVector, charLength, clampDateKey, classifyDocumentText, cosineSimilarity, dateKeyOf, daysUntil, deadlineKindLabel, embeddingScore, endOfWeekKey, extractPdfText, formatDateKeyLong, formatDateKeyShort, formatDday, formatDeadlineForUser, formatDeadlineInSourceZone, formatDeadlineLocal, formatDeadlineTitle, formatDeterministicCitation, formatExportDescription, formatExportSummary, formatLocalDate, formatRelativeActivityTime, formatScheduleText, formatTimeOfDay, formatYearMonth, freshnessScore, isDateKey, isDeterministicCitationFormat, isNearDuplicate, isNonEmpty, isPollExpired, isTimeOfDay, isWithinContextLimit, keywordScore, l2Normalize, locateQuote, matchQuoteKeys, mondayFirstWeekday, monthGridKeys, normalizePdfText, normalizeQuote, pairPenalty, parseAuthorName, parseDateKey, pickNextDeadline, pollDelay, pollDelayForElapsed, quoteSelectorFromRange, remainingMs, requiresAuth, rerank, startOfWeekKey, timeOfDayOptions, toBasicUtcStamp, toBibtex, toCslJson, toRis, todayDateKey, weightedSum };
